import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarCheck,
  ChevronRight,
  DollarSign,
  Download,
  Pencil,
  RefreshCw,
  Trash2,
  TrendingUp,
  Upload,
  UserMinus,
  UserCheck,
  UserX,
  Users,
  Wallet,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import FoodCashPanel from './FoodCashPanel';
import { useI18n } from '../../context/useI18n';
import '../../styles/finance-board.css';

const MODULE_COLORS = {
  d28d: '#65a30d',
  food: '#ea580c',
  training: '#2563eb',
  spiritual: '#a855f7',
};

const PROGRAM_PALETTE = [
  '#65a30d', '#ea580c', '#2563eb', '#db2777', '#0891b2', '#ca8a04', '#7c3aed', '#dc2626',
];

function formatMoney(n, currency) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-CO', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'COP',
      maximumFractionDigits: currency === 'USD' ? 2 : 0,
    }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString()}`;
  }
}

function shortMoney(n, currency) {
  const v = Number(n) || 0;
  if (currency === 'USD') return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toLocaleString('es-CO')}`;
}

const DRILL_CHAIN_GLOBAL = ['module', 'program', 'plan', 'user'];
const DRILL_CHAIN_SCOPED = ['program', 'plan', 'user'];

function expenseDayMonth(iso) {
  const match = String(iso || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return { year: match[1], month: String(Number(match[2])), day: match[3] };
}

function foodAlertTarget(code) {
  if (
    code === 'pending_payments'
    || code === 'expiring_7d'
    || code === 'expiring_15d'
    || code === 'expiring_30d'
    || code === 'billing_collection_gap'
    || code === 'billing_review'
  ) return 'vigencias';
  if (code === 'expenses_missing') return 'expenses';
  return null;
}

const MODULE_TITLES = {
  d28d: 'Finanzas D28D',
  food: 'Finanzas FOOD',
  training: 'Finanzas Training',
};

export default function SuperAdminFinanceDashboard({
  onBack,
  moduleScope = null,
  coachScoped = false,
  onOpenVigencias = null,
}) {
  const { t } = useI18n();
  const scoped = ['d28d', 'food', 'training'].includes(moduleScope) ? moduleScope : null;
  const drillChain = scoped ? DRILL_CHAIN_SCOPED : DRILL_CHAIN_GLOBAL;
  const [period, setPeriod] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currency, setCurrency] = useState('COP');
  const [summary, setSummary] = useState(null);
  const [drill, setDrill] = useState(null);
  const [level, setLevel] = useState(scoped ? 'program' : 'module');
  const [filters, setFilters] = useState({
    module: scoped || null,
    program: null,
    gym_id: null,
    plan: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [importing, setImporting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeMsg, setCloseMsg] = useState('');
  const [expenseDraft, setExpenseDraft] = useState({ id: '', date: '', amount: '', category: '', note: '' });
  const [expenseSaving, setExpenseSaving] = useState(false);
  const loadGen = useRef(0);
  useEffect(() => {
    setLevel(scoped ? 'program' : 'module');
    setFilters({ module: scoped || null, program: null, gym_id: null, plan: null });
    if (scoped === 'food') setCurrency('COP');
  }, [scoped]);

  const params = useMemo(() => ({
    period,
    date_from: period === 'custom' ? dateFrom || undefined : undefined,
    date_to: period === 'custom' ? dateTo || undefined : undefined,
    currency,
    level,
    scope_module: scoped || undefined,
    module: scoped || filters.module || undefined,
    program: filters.program || undefined,
    gym_id: filters.gym_id || undefined,
    plan: filters.plan || undefined,
  }), [period, dateFrom, dateTo, currency, level, filters, scoped]);

  const load = useCallback(async () => {
    if (period === 'custom' && (!dateFrom || !dateTo)) {
      setLoading(false);
      return;
    }
    const gen = ++loadGen.current;
    setLoading(true);
    setError('');
    const fetchBoard = () => Promise.all([
      api.get('/platform/finance/summary', {
        params: {
          period,
          date_from: period === 'custom' ? dateFrom || undefined : undefined,
          date_to: period === 'custom' ? dateTo || undefined : undefined,
          currency,
          scope_module: scoped || undefined,
        },
      }),
      api.get('/platform/finance/drill', { params }),
    ]);
    try {
      let s;
      let d;
      try {
        [s, d] = await fetchBoard();
      } catch (err) {
        const dropped = !err?.response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error');
        if (!dropped || gen !== loadGen.current) throw err;
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (gen !== loadGen.current) return;
        [s, d] = await fetchBoard();
      }
      if (gen !== loadGen.current) return;
      setSummary(s.data);
      setDrill(d.data);
    } catch (err) {
      if (gen !== loadGen.current || err?.code === 'ERR_CANCELED') return;
      setError(err?.response?.data?.error || err.message || 'Error cargando finanzas');
      setSummary(null);
      setDrill(null);
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, [period, dateFrom, dateTo, currency, params, scoped]);

  useEffect(() => {
    load();
  }, [load]);

  const programBars = useMemo(() => {
    const rows = summary?.by_program || [];
    return rows.slice(0, 8).map((r, i) => ({
      name: r.label || r.program_id,
      revenue: Number(r.revenue || 0),
      count: Number(r.count || 0),
      program_id: r.program_id,
      module: r.module,
      fill: MODULE_COLORS[r.module] || PROGRAM_PALETTE[i % PROGRAM_PALETTE.length],
    }));
  }, [summary]);

  const foodRoster = summary?.food_roster || summary?.food_nest?.snapshot?.roster || null;

  const licenseBars = useMemo(() => {
    if (scoped === 'food' && foodRoster) {
      return [
        {
          name: t('finance.kpi.food_activo', 'Activo'),
          activos: Number(foodRoster.activo || 0),
          inactivos: 0,
          module: 'food',
        },
        {
          name: t('finance.kpi.food_revisado', 'Revisado'),
          activos: Number(foodRoster.revisado || 0),
          inactivos: 0,
          module: 'food',
        },
        {
          name: t('finance.kpi.food_pending', 'Pendiente'),
          activos: Number(foodRoster.pago_pendiente || 0),
          inactivos: 0,
          module: 'food',
        },
        {
          name: t('finance.kpi.food_inactive', 'Inactivo'),
          activos: Number(foodRoster.inactivo || 0),
          inactivos: 0,
          module: 'food',
        },
      ];
    }
    const a = summary?.active_by_module || {};
    const i = summary?.inactive_by_module || {};
    const mods = scoped ? [scoped] : ['d28d', 'food', 'training'];
    return mods.map((m) => ({
      name: m.toUpperCase(),
      activos: Number(a[m] || 0),
      inactivos: Number(i[m] || 0),
      module: m,
    }));
  }, [summary, scoped, foodRoster, t]);

  const drillBars = useMemo(() => (
    (drill?.drill?.rows || []).map((r, i) => ({
      ...r,
      name: String(r.label || r.key || '').slice(0, 28),
      fill: MODULE_COLORS[r.module] || PROGRAM_PALETTE[i % PROGRAM_PALETTE.length],
    }))
  ), [drill]);

  const onDrillRow = (row) => {
    if (!row) return;
    if (level === 'module') {
      setFilters({ module: row.module || row.key, program: null, gym_id: null, plan: null });
      setLevel('program');
      return;
    }
    if (level === 'program') {
      setFilters((f) => ({
        ...f,
        module: scoped || row.module || f.module,
        program: row.program_id || row.key,
        plan: null,
      }));
      setLevel('plan');
      return;
    }
    if (level === 'plan') {
      setFilters((f) => ({ ...f, plan: row.plan || row.key }));
      setLevel('user');
    }
  };

  const onProgramBarClick = (bar) => {
    if (!bar?.program_id) return;
    setFilters({
      module: scoped || bar.module || null,
      program: bar.program_id,
      gym_id: null,
      plan: null,
    });
    setLevel('plan');
  };

  const resetDrill = () => {
    setFilters({ module: scoped || null, program: null, gym_id: null, plan: null });
    setLevel(scoped ? 'program' : 'module');
  };

  const goUp = () => {
    const idx = drillChain.indexOf(level);
    if (idx <= 0) {
      resetDrill();
      return;
    }
    const next = drillChain[idx - 1];
    if (next === 'module') setFilters({ module: null, program: null, gym_id: null, plan: null });
    if (next === 'program') {
      setFilters((f) => ({
        ...f,
        module: scoped || f.module,
        program: null,
        plan: null,
        gym_id: null,
      }));
    }
    if (next === 'plan') setFilters((f) => ({ ...f, plan: null }));
    setLevel(next);
  };

  const downloadTemplate = async () => {
    const res = await api.get('/platform/finance/expenses/template', {
      params: { scope_module: scoped || undefined },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = scoped ? `plantilla_egresos_${scoped}.xlsx` : 'plantilla_egresos_financieros.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const res = await api.get('/platform/finance/export.csv', {
      params,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = scoped ? `finance_${scoped}.csv` : 'finance_drill.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportBillingCsv = async () => {
    const res = await api.get('/platform/finance/billing.csv', {
      params,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facturables_${scoped || 'global'}_${summary?.range?.start?.slice(0, 10) || 'rango'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const closeMonth = async () => {
    if (!summary?.closure?.closable || summary?.closure?.locked || !scoped) return;
    setClosing(true);
    setCloseMsg('');
    try {
      const res = await api.post('/platform/finance/close', {
        period,
        date_from: period === 'custom' ? dateFrom || undefined : undefined,
        date_to: period === 'custom' ? dateTo || undefined : undefined,
        currency,
        scope_module: scoped,
      });
      setCloseMsg(res.data?.locked ? 'Cierre mensual guardado y bloqueado.' : 'Cierre guardado.');
      await load();
    } catch (err) {
      setCloseMsg(err?.response?.data?.error || err.message || 'No se pudo cerrar el mes');
    } finally {
      setClosing(false);
    }
  };

  const onImportFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/platform/finance/expenses/import', fd, {
        params: { scope_module: scoped || undefined },
      });
      const saved = Number(res.data?.imported || 0);
      const skipped = Number(res.data?.skipped || 0);
      const dates = Array.isArray(res.data?.dates) ? res.data.dates : [];
      setImportMsg(
        saved
          ? `Guardados ${saved}${skipped ? ` · ${skipped} filas sin fecha o sin valor` : ''}. Fechas: ${dates.slice(0, 3).join(', ')}${dates.length > 3 ? '…' : ''}`
          : `No se guardó ninguna fila${skipped ? ` (${skipped} sin fecha o sin valor)` : ''}. Descarga la plantilla: columnas fecha y monto_cop.`,
      );
      await load();
    } catch (err) {
      setImportMsg(err?.response?.data?.error || err.message || 'Error import');
    } finally {
      setImporting(false);
    }
  };

  const kpis = summary?.kpis || {};
  const isCoachBoard = Boolean(coachScoped) || summary?.trainer_scope != null;
  const saveExpense = async (event) => {
    event.preventDefault();
    setExpenseSaving(true);
    setImportMsg('');
    try {
      const body = {
        date: expenseDraft.date,
        amount: expenseDraft.amount,
        category: expenseDraft.category,
        note: expenseDraft.note,
        scope_module: scoped || 'plataforma',
      };
      if (expenseDraft.id) {
        await api.patch(`/platform/finance/expenses/${expenseDraft.id}`, body, {
          params: { scope_module: scoped || undefined },
        });
      } else {
        await api.post('/platform/finance/expenses', body, {
          params: { scope_module: scoped || undefined },
        });
      }
      setExpenseDraft({ id: '', date: '', amount: '', category: '', note: '' });
      await load();
    } catch (err) {
      setImportMsg(err?.response?.data?.error || err.message || 'No se pudo guardar el rubro');
    } finally {
      setExpenseSaving(false);
    }
  };

  const removeExpense = async (row) => {
    if (!row?.id) return;
    if (!window.confirm('¿Borrar este rubro?')) return;
    setExpenseSaving(true);
    try {
      await api.delete(`/platform/finance/expenses/${row.id}`, {
        params: { scope_module: scoped || undefined },
      });
      if (expenseDraft.id === row.id) setExpenseDraft({ id: '', date: '', amount: '', category: '', note: '' });
      await load();
    } catch (err) {
      setImportMsg(err?.response?.data?.error || err.message || 'No se pudo borrar');
    } finally {
      setExpenseSaving(false);
    }
  };

  const foodKpiMode = scoped === 'food' && foodRoster && period === 'month';
  const kpiCards = [
    {
      id: 'revenue',
      label: t('finance.kpi.collected', 'Recaudo'),
      hint: foodKpiMode
        ? 'Mes completo. La misma cifra que Pago del mes en caja'
        : t('finance.kpi.collected_hint', 'Pagos recibidos en el rango'),
      value: formatMoney(kpis.revenue, currency),
      Icon: TrendingUp,
      accent: '#65a30d',
    },
    {
      id: 'billable',
      label: t('finance.kpi.billable', 'Facturables'),
      hint: t('finance.kpi.billable_hint', 'Más de 4 días vigentes en el rango'),
      value: String(kpis.billable ?? '—'),
      Icon: CalendarCheck,
      accent: '#7c3aed',
    },
    {
      id: 'expenses',
      label: t('finance.kpi.expenses', 'Egresos'),
      hint: t('finance.kpi.expenses_hint', 'Excel'),
      value: formatMoney(kpis.expenses, currency),
      Icon: Wallet,
      accent: '#ea580c',
    },
    {
      id: 'net',
      label: t('finance.kpi.net', 'Neto'),
      hint: foodKpiMode
        ? t('finance.kpi.food_net_hint', 'Ingresos de activos − egresos')
        : t('finance.kpi.net_hint', 'Ingresos − egresos'),
      value: formatMoney(kpis.net, currency),
      Icon: DollarSign,
      accent: '#2563eb',
    },
    foodKpiMode
      ? {
        id: 'food_licenses',
        label: t('finance.kpi.food_licenses', 'Licencias'),
        hint: t('finance.kpi.food_licenses_hint', 'Misma pestaña Licencias de Vigencias Food'),
        value: String(foodRoster.licenses ?? '—'),
        Icon: Users,
        accent: '#16a34a',
      }
      : {
        id: 'active',
        label: t('finance.kpi.active', 'Activos'),
        hint: t('finance.kpi.active_hint', 'Licencias vigentes'),
        value: String(kpis.active ?? '—'),
        Icon: UserCheck,
        accent: '#16a34a',
      },
    foodKpiMode
      ? {
        id: 'food_activo',
        label: t('finance.kpi.food_activo', 'Activos'),
        hint: t('finance.kpi.food_activo_hint', 'Mismo filtro Activo de Vigencias Food'),
        value: String(foodRoster.activo ?? '—'),
        Icon: UserCheck,
        accent: '#0891b2',
      }
      : {
        id: 'inactive',
        label: t('finance.kpi.inactive', 'Inactivos'),
        hint: t('finance.kpi.inactive_hint', 'Sin vigencia'),
        value: String(kpis.inactive ?? '—'),
        Icon: UserX,
        accent: '#64748b',
      },
    foodKpiMode
      ? {
        id: 'food_pending',
        label: t('finance.kpi.food_pending', 'Pendientes'),
        hint: t('finance.kpi.food_pending_hint', 'Pago pendiente en Vigencias Food'),
        value: String(foodRoster.pago_pendiente ?? '—'),
        Icon: RefreshCw,
        accent: '#ca8a04',
      }
      : {
        id: 'withdrawals',
        label: t('finance.kpi.withdrawals', 'Bajas'),
        hint: t('finance.kpi.withdrawals_hint', 'Periodo'),
        value: String(kpis.withdrawals ?? '—'),
        Icon: UserMinus,
        accent: '#dc2626',
      },
    ...(foodKpiMode
      ? [{
        id: 'food_expiring',
        label: t('finance.kpi.food_expiring', 'Por vencer'),
        hint: t('finance.kpi.food_expiring_hint', '0 a 30 días, igual que Vigencias Food'),
        value: String(foodRoster.expiring ?? '—'),
        Icon: UserX,
        accent: '#dc2626',
      }]
      : []),
  ];

  const breadcrumb = scoped
    ? [
      { id: 'program', label: MODULE_TITLES[scoped] || scoped.toUpperCase(), active: level === 'program' },
      { id: 'plan', label: filters.program || 'Planes', active: level === 'plan' },
      { id: 'user', label: filters.plan || 'Usuarios', active: level === 'user' },
    ]
    : [
      { id: 'module', label: 'Módulos', active: level === 'module' },
      { id: 'program', label: filters.module ? filters.module.toUpperCase() : 'Programas', active: level === 'program' },
      { id: 'plan', label: filters.program || 'Planes', active: level === 'plan' },
      { id: 'user', label: filters.plan || 'Usuarios', active: level === 'user' },
    ];

  return (
    <div className="dashboard-main-view finance-board finance-board--compact space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {onBack ? (
            <button type="button" className="btn-secondary text-sm mb-2" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              {t('finance.back', 'Volver al inicio')}
            </button>
          ) : null}
          <h1 className="d28d-page-title text-xl flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-lime-400" aria-hidden />
            {isCoachBoard
              ? t('finance.title.coach', 'Finanzas de mis afiliados')
              : scoped
                ? t(`finance.title.${scoped}`, MODULE_TITLES[scoped])
                : t('finance.title', 'Resumen financiero')}
          </h1>
          <p className="d28d-text-muted text-xs mt-1">
            {isCoachBoard
              ? t('finance.subtitle_coach', 'Mismo tablero Training, solo ingresos y KPIs de tus afiliados.')
              : scoped
                ? t('finance.subtitle_scoped', 'Solo datos de este módulo. Haz clic en barras para profundizar.')
                : t('finance.subtitle_compact', 'Haz clic en las barras para profundizar (módulo → programa → plan → usuario).')}
          </p>
          {summary?.range ? (
            <p className="d28d-text-muted text-xs mt-1">
              Rango: {new Date(summary.range.start).toLocaleDateString('es-CO')} – {new Date(summary.range.end).toLocaleDateString('es-CO')}
              {' · '}Zona: America/Bogota
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select className="input" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Periodo">
            <option value="month">{t('finance.period.month', 'Mes actual')}</option>
            <option value="previous">{t('finance.period.previous', 'Mes anterior')}</option>
            <option value="last3">{t('finance.period.last3', 'Últimos 3 meses')}</option>
            <option value="last6">{t('finance.period.last6', 'Últimos 6 meses')}</option>
            <option value="last12">{t('finance.period.last12', 'Últimos 12 meses')}</option>
            <option value="custom">{t('finance.period.custom', 'Rango personalizado')}</option>
          </select>
          {period === 'custom' ? (
            <>
              <input
                type="date"
                className="input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                aria-label="Fecha desde"
              />
              <input
                type="date"
                className="input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                aria-label="Fecha hasta"
              />
            </>
          ) : null}
          <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Moneda">
            <option value="COP">COP</option>
            {scoped === 'food' ? null : <option value="USD">USD</option>}
          </select>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 inline mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('finance.refresh', 'Actualizar')}
          </button>
        </div>
      </header>

      {error ? (
        <div className="card p-3 text-red-700 bg-red-50 ui-light-panel surface-readable text-sm">{error}</div>
      ) : null}

      <div className="finance-top-grid">
        <div className="finance-kpi-stack">
          {kpiCards.map((kpi) => (
            <article
              key={kpi.id}
              className="finance-kpi-card finance-kpi-card--row d28d-admin-surface ui-light-panel surface-readable"
              style={{ borderLeftColor: kpi.accent }}
            >
              <span className="finance-kpi-card__icon" style={{ background: `${kpi.accent}18`, color: kpi.accent }}>
                <kpi.Icon className="w-4 h-4" aria-hidden />
              </span>
              <div className="finance-kpi-card__meta">
                <p className="finance-kpi-card__label">{kpi.label}</p>
                <p className="finance-kpi-card__hint">{kpi.hint}</p>
              </div>
              <p className="finance-kpi-card__value">{loading && !summary ? '…' : kpi.value}</p>
            </article>
          ))}
          {scoped === 'food' && !isCoachBoard ? (
            <article
              className="finance-kpi-card finance-kpi-card--row d28d-admin-surface ui-light-panel surface-readable"
              style={{ borderLeftColor: Number(kpis.roi) < 0 ? '#dc2626' : '#16a34a' }}
            >
              <span className="finance-kpi-card__icon" style={{ background: '#16a34a18', color: '#16a34a' }}>
                <TrendingUp className="w-4 h-4" aria-hidden />
              </span>
              <div className="finance-kpi-card__meta">
                <p className="finance-kpi-card__label">ROI</p>
                <p className="finance-kpi-card__hint finance-kpi-card__hint--wrap">(pago del mes − egresos) / egresos</p>
              </div>
              <p className={`finance-kpi-card__value ${Number(kpis.roi) < 0 ? 'text-red-700' : ''}`}>
                {loading && !summary ? '…' : (kpis.roi == null ? '—' : `${Number(kpis.roi).toLocaleString('es-CO')}%`)}
              </p>
            </article>
          ) : null}
        </div>

        <div className="finance-charts-col">
          {scoped === 'food' ? (
            <>
              <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
                <h2 className="font-semibold text-sm m-0">Nuevos, recurrentes y los que no siguieron</h2>
                <p className="text-xs d28d-text-muted m-0 mt-1 mb-2">
                  Nuevo: primera vigencia en este mes. Recurrente: ya estaba y sigue activo. No siguieron: se venció en este mes.
                </p>
                <div className="finance-chart-box finance-chart-box--licenses">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Nuevos', value: Number(foodRoster?.nuevos || 0), fill: '#65a30d' },
                        { name: 'Recurrentes', value: Number(foodRoster?.recurrentes || 0), fill: '#0369a1' },
                        { name: 'No siguieron', value: Number(foodRoster?.no_siguieron || 0), fill: '#b45309' },
                      ]}
                      margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#334155' }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                      <Tooltip />
                      <Bar dataKey="value" name="Personas" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="value" position="top" fill="#111827" fontSize={12} />
                        {[
                          { name: 'Nuevos', fill: '#65a30d' },
                          { name: 'Recurrentes', fill: '#0369a1' },
                          { name: 'No siguieron', fill: '#b45309' },
                        ].map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
              <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
                <h2 className="font-semibold text-sm m-0">Dinero del mes</h2>
                <p className="text-xs d28d-text-muted m-0 mt-1 mb-2">
                  Recaudo: un mes del plan de cada activo, el día en que se activó. Neto = ese ingreso − egresos.
                </p>
                <div className="finance-chart-box finance-chart-box--programs">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Recaudo', value: Number(kpis.revenue || 0), label: shortMoney(kpis.revenue, currency) },
                        { name: 'Egresos', value: Number(kpis.expenses || 0), label: shortMoney(kpis.expenses, currency) },
                        { name: 'Neto', value: Number(kpis.net || 0), label: shortMoney(kpis.net, currency) },
                      ]}
                      margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#334155' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => shortMoney(v, currency)} />
                      <Tooltip formatter={(v) => formatMoney(v, currency)} />
                      <Bar dataKey="value" name="COP" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="label" position="top" fill="#111827" fontSize={11} />
                        <Cell fill="#65a30d" />
                        <Cell fill="#ea580c" />
                        <Cell fill="#0369a1" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            </>
          ) : (
          <>
          <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="font-semibold text-sm m-0">{t('finance.chart.programs', 'Recaudo por programa')}</h2>
              <span className="text-[10px] uppercase tracking-wide text-slate-500">click → drill</span>
            </div>
            <div className="finance-chart-box finance-chart-box--programs">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={programBars} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => shortMoney(v, currency)} />
                  <YAxis type="category" dataKey="name" width={88} tick={{ fontSize: 10, fill: '#334155' }} />
                  <Tooltip
                    formatter={(v, name) => (name === 'revenue' ? formatMoney(v, currency) : v)}
                    labelFormatter={(l) => String(l)}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Recaudo"
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(data) => onProgramBarClick(data?.payload || data)}
                  >
                    {programBars.map((entry) => (
                      <Cell key={entry.program_id} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
            <h2 className="font-semibold text-sm mb-2">
              {scoped === 'food' && foodRoster
                ? t('finance.chart.food_roster', 'Licencias Food (espejo Vigencias)')
                : t('finance.chart.licenses', 'Licencias por módulo')}
            </h2>
            <div className="finance-chart-box finance-chart-box--licenses">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={licenseBars} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#475569' }} />
                  <Tooltip />
                  <Bar dataKey="activos" name="Activos" fill="#65a30d" radius={[4, 4, 0, 0]} />
                  {scoped === 'food' && foodRoster ? null : (
                    <Bar dataKey="inactivos" name="Inactivos" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
          </>
          )}
        </div>
        {!isCoachBoard ? (
          <div className="finance-expenses-col">
            <section id="finance-expenses" className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className="font-semibold text-sm m-0">{t('finance.expenses.title', 'Egresos')}</h2>
                  <p className="text-xs d28d-text-muted m-0 mt-0.5">
                    Pendientes: {kpis.pending_confirms ?? 0} · Egresos periodo: {kpis.expenses_count ?? 0}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button type="button" className="btn-secondary text-sm" onClick={downloadTemplate}>
                    <Download className="w-4 h-4 inline mr-1" />
                    Plantilla
                  </button>
                  <label className="btn-primary text-sm cursor-pointer inline-flex items-center gap-1">
                    <Upload className="w-4 h-4" />
                    {importing ? 'Importando…' : 'Subir'}
                    <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onImportFile} disabled={importing} />
                  </label>
                  {importMsg ? <span className="text-xs d28d-text-muted">{importMsg}</span> : null}
                </div>
              </div>
              <form onSubmit={saveExpense} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end mt-3">
                <label className="text-xs text-slate-600">
                  Fecha
                  <input id="finance-expense-date" type="date" className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white" value={expenseDraft.date} onChange={(e) => setExpenseDraft((prev) => ({ ...prev, date: e.target.value }))} required />
                </label>
                <label className="text-xs text-slate-600">
                  Valor
                  <input inputMode="numeric" className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white" value={expenseDraft.amount} onChange={(e) => setExpenseDraft((prev) => ({ ...prev, amount: e.target.value }))} required />
                </label>
                <label className="text-xs text-slate-600">
                  Rubro
                  <input className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white" value={expenseDraft.category} onChange={(e) => setExpenseDraft((prev) => ({ ...prev, category: e.target.value }))} placeholder="Nómina, servidores…" />
                </label>
                <label className="text-xs text-slate-600">
                  Nota
                  <input className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white" value={expenseDraft.note} onChange={(e) => setExpenseDraft((prev) => ({ ...prev, note: e.target.value }))} />
                </label>
                <div className="flex gap-2">
                  <button type="submit" className="btn-primary text-sm" disabled={expenseSaving}>
                    {expenseDraft.id ? 'Guardar' : 'Agregar rubro'}
                  </button>
                  {expenseDraft.id ? (
                    <button type="button" className="btn-secondary text-sm" onClick={() => setExpenseDraft({ id: '', date: '', amount: '', category: '', note: '' })}>
                      Cancelar
                    </button>
                  ) : null}
                </div>
              </form>
              {(summary?.expenses_rows || []).length ? (
                <div className="overflow-x-auto mt-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-stone-200">
                        <th className="py-1 pr-2">Día / mes</th>
                        <th className="py-1 pr-2">Valor</th>
                        <th className="py-1 pr-2">Rubro</th>
                        <th className="py-1 pr-2">Nota</th>
                        <th className="py-1"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.expenses_rows.map((row, idx) => {
                        const when = expenseDayMonth(row.date);
                        return (
                        <tr key={row.id || `${row.date}-${idx}`} className="border-b border-stone-100">
                          <td className="py-1 pr-2 whitespace-nowrap tabular-nums">
                            {when ? (
                              <>
                                <span>{when.day}/{when.month.padStart(2, '0')}</span>
                                <span className="block text-[11px] leading-tight text-slate-500">mes {when.month}</span>
                              </>
                            ) : (row.date || '—')}
                            {row.in_period ? null : (
                              <span className="block text-[11px] leading-tight text-amber-700">fuera de este mes</span>
                            )}
                          </td>
                          <td className="py-1 pr-2">{formatMoney(row.amount, currency)}</td>
                          <td className="py-1 pr-2">{row.category || '—'}</td>
                          <td className="py-1 pr-2">{row.note || '—'}</td>
                          <td className="py-1 text-right whitespace-nowrap">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-700 hover:bg-stone-100 disabled:opacity-40"
                              title="Editar"
                              aria-label="Editar"
                              disabled={!row.id || expenseSaving}
                              onClick={() => setExpenseDraft({
                                id: row.id,
                                date: String(row.date || '').slice(0, 10),
                                amount: String(row.amount || ''),
                                category: row.category || '',
                                note: row.note || '',
                              })}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-700 hover:bg-red-50 disabled:opacity-40"
                              title="Eliminar"
                              aria-label="Eliminar"
                              disabled={!row.id || expenseSaving}
                              onClick={() => removeExpense(row)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs d28d-text-muted m-0 mt-3">No hay egresos guardados en este módulo.</p>
              )}
            </section>
          </div>
        ) : null}
      </div>

      {scoped === 'food' && !isCoachBoard ? (
        <FoodCashPanel
          period={period}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      ) : null}

      {summary?.comparison ? (
        <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
          <h2 className="font-semibold text-sm m-0">
            {t('finance.comparison.title', 'Comparación con el periodo anterior equivalente')}
          </h2>
          <p className="text-xs d28d-text-muted mt-1 mb-0">
            Anterior: {new Date(summary.comparison.range.start).toLocaleDateString('es-CO')} – {
              new Date(summary.comparison.range.end).toLocaleDateString('es-CO')
            }
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
            {[
              ['billable', 'Facturables', false],
              ['active', 'Activos al cierre', false],
              ['collected', 'Recaudo', true],
              ['expenses', 'Egresos', true],
              ['net', 'Neto', true],
            ].map(([key, label, money]) => {
              const row = summary.comparison[key] || {};
              const deltaLabel = row.delta_pct == null
                ? `${row.delta >= 0 ? '+' : ''}${row.delta || 0}`
                : `${row.delta_pct >= 0 ? '+' : ''}${row.delta_pct}%`;
              return (
                <div key={key} className="border-b border-stone-200 pb-2">
                  <p className="text-xs text-slate-500 m-0">{label}</p>
                  <p className="font-semibold m-0 mt-1">
                    {money ? formatMoney(row.current, currency) : row.current}
                  </p>
                  <p className={`text-xs m-0 mt-1 ${Number(row.delta) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {deltaLabel} vs anterior
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {scoped === 'food' && foodRoster ? (
        <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
          <h2 className="font-semibold text-sm m-0">
            {t('finance.renewed.title', 'Renovaciones del periodo')}
          </h2>
          <p className="text-xs d28d-text-muted m-0 mt-0.5">
            {t(
              'finance.renewed.subtitle',
              'Solo usuarios finales de Vigencias Food que renovaron en este rango.',
            )}
          </p>
          {(foodRoster.renewed_rows || []).length === 0 ? (
            <p className="text-sm mt-3 m-0">{t('finance.renewed.empty', 'Nadie renovó en este periodo.')}</p>
          ) : (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-stone-200">
                    <th className="py-2 pr-2">Nombre</th>
                    <th className="py-2 pr-2">Email</th>
                    <th className="py-2 pr-2">Tipo</th>
                    <th className="py-2 pr-2">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {(foodRoster.renewed_rows || []).map((row) => (
                    <tr key={row.user_id} className="border-b border-stone-100">
                      <td className="py-2 pr-2">{row.name || '—'}</td>
                      <td className="py-2 pr-2">{row.email || '—'}</td>
                      <td className="py-2 pr-2">
                        {row.kind === 'extension'
                          ? t('finance.renewed.kind_extension', 'Extensión')
                          : t('finance.renewed.kind_new_row', 'Nueva suscripción')}
                      </td>
                      <td className="py-2 pr-2">
                        {row.when ? new Date(row.when).toLocaleDateString('es-CO') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold text-sm m-0">
              {t('finance.billing.title', 'Soporte de facturación mensual')}
            </h2>
            <p className="text-xs d28d-text-muted m-0 mt-1">
              Una persona por módulo · cuenta si tuvo más de 4 días calendario de vigencia.
              Recaudo y facturación se muestran separados.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary text-sm" onClick={exportBillingCsv}>
              <Download className="w-4 h-4 inline mr-1" />
              Exportar soporte
            </button>
            {!isCoachBoard && scoped && summary?.closure?.closable ? (
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={closeMonth}
                disabled={closing || summary?.closure?.locked}
              >
                <CalendarCheck className="w-4 h-4 inline mr-1" />
                {summary?.closure?.locked ? 'Mes cerrado' : (closing ? 'Cerrando…' : 'Cerrar mes')}
              </button>
            ) : null}
          </div>
        </div>
        {closeMsg ? <p className="text-xs mt-2 mb-0">{closeMsg}</p> : null}
        <div className="flex flex-wrap gap-4 mt-3 text-sm">
          <span><strong>{summary?.billing?.count ?? 0}</strong> facturables</span>
          <span><strong>{formatMoney(summary?.billing?.amount || 0, currency)}</strong> valor de lista</span>
          <span><strong>{(summary?.billing?.rows || []).filter((row) => !row.billable).length}</strong> excluidos</span>
          <span><strong>{summary?.billing?.review?.length || 0}</strong> por revisar</span>
        </div>
        <div className="overflow-x-auto mt-3 finance-drill-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="py-2 pr-2">Usuario</th>
                <th className="py-2 pr-2">Módulo</th>
                <th className="py-2 pr-2">Plan</th>
                <th className="py-2 pr-2">Días</th>
                <th className="py-2 pr-2">Resultado</th>
                <th className="py-2">Tarifa</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.billing?.rows || []).slice(0, 100).map((row) => (
                <tr key={`${row.module}-${row.user_id}`} className="border-b border-stone-100">
                  <td className="py-1.5 pr-2">
                    <div className="font-medium">{row.name || '—'}</div>
                    <div className="text-xs text-slate-500">{row.email || `ID ${row.user_id}`}</div>
                  </td>
                  <td className="py-1.5 pr-2 uppercase">{row.module}</td>
                  <td className="py-1.5 pr-2">{row.plan || '—'}</td>
                  <td className="py-1.5 pr-2">{row.coverage_days}</td>
                  <td className="py-1.5 pr-2">{row.billable ? 'Facturable' : 'Excluido'}</td>
                  <td className="py-1.5">{formatMoney(row.tariff, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(summary?.alerts || []).length ? (
        <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable">
          <h2 className="font-semibold text-sm m-0 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" aria-hidden />
            {t('finance.alerts.title', 'Alertas para decisión')}
          </h2>
          <div className="grid md:grid-cols-2 gap-2 mt-3">
            {summary.alerts.map((alert) => (
              <div key={alert.code} className="border border-stone-200 rounded-lg p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{alert.label}</span>
                  <strong>{alert.count}</strong>
                </div>
                <p className="text-xs text-slate-500 m-0 mt-1">
                  Severidad: {alert.severity === 'high' ? 'alta' : 'media'}
                </p>
                {scoped === 'food' && foodAlertTarget(alert.code) === 'vigencias' && onOpenVigencias ? (
                  <button
                    type="button"
                    className="btn-secondary text-xs mt-2"
                    onClick={() => onOpenVigencias()}
                  >
                    Ir a Vigencias
                  </button>
                ) : null}
                {scoped === 'food' && foodAlertTarget(alert.code) === 'expenses' ? (
                  <button
                    type="button"
                    className="btn-secondary text-xs mt-2"
                    onClick={() => {
                      document.getElementById('finance-expense-date')?.focus();
                      document.getElementById('finance-expenses')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  >
                    Cargar egreso
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {summary?.audit?.total ? (
            <p className="text-xs d28d-text-muted mt-3 mb-0">
              Auditoría: {summary.audit.total} señales · {
                Object.entries(summary.audit.by_code || {})
                  .map(([code, count]) => `${code}: ${count}`)
                  .join(' · ')
              }
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="card p-3 d28d-admin-surface ui-light-panel surface-readable space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-sm m-0">{t('finance.drill.title', 'Drill-down')}</h2>
            <nav className="finance-breadcrumb" aria-label="Drill path">
              {breadcrumb.map((crumb, idx) => (
                <span key={crumb.id} className={crumb.active ? 'is-active' : ''}>
                  {idx > 0 ? <ChevronRight className="w-3 h-3 inline mx-0.5 opacity-50" /> : null}
                  {crumb.label}
                </span>
              ))}
            </nav>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary text-sm" onClick={goUp} disabled={level === (scoped ? 'program' : 'module')}>
              Subir
            </button>
            <button type="button" className="btn-secondary text-sm" onClick={resetDrill}>
              Reiniciar
            </button>
            <button type="button" className="btn-secondary text-sm" onClick={exportCsv}>
              <Download className="w-4 h-4 inline mr-1" />
              CSV
            </button>
          </div>
        </div>

        <div className="finance-drill-grid">
          <div className="finance-chart-box finance-chart-box--drill">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={drillBars} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} interval={0} angle={-18} textAnchor="end" height={48} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={(v) => shortMoney(v, currency)} />
                <Tooltip formatter={(v) => formatMoney(v, currency)} />
                <Bar
                  dataKey="amount"
                  name="Monto"
                  radius={[6, 6, 0, 0]}
                  cursor={level === 'user' ? 'default' : 'pointer'}
                  onClick={(data) => level !== 'user' && onDrillRow(data?.payload || data)}
                >
                  {drillBars.map((entry) => (
                    <Cell key={entry.key} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="finance-chart-box finance-chart-box--trend">
            <p className="finance-mini-title">{t('finance.chart.trend', 'Tendencia del rango')}</p>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={summary?.series_monthly || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="financeTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#65a30d" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#65a30d" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 9, fill: '#64748b' }} tickFormatter={(v) => shortMoney(v, currency)} width={44} />
                <Tooltip formatter={(v) => formatMoney(v, currency)} />
                <Area type="monotone" dataKey="total" stroke="#65a30d" fill="url(#financeTrendFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="overflow-x-auto finance-drill-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="py-2 pr-2">Concepto</th>
                <th className="py-2 pr-2">Monto</th>
                <th className="py-2 pr-2">Movimientos</th>
                <th className="py-2"> </th>
              </tr>
            </thead>
            <tbody>
              {(drill?.drill?.rows || []).map((row) => (
                <tr key={row.key} className="border-b border-stone-100">
                  <td className="py-1.5 pr-2">{row.label}</td>
                  <td className="py-1.5 pr-2 font-semibold">{formatMoney(row.amount, currency)}</td>
                  <td className="py-1.5 pr-2">{row.count}</td>
                  <td className="py-1.5">
                    {level !== 'user' ? (
                      <button type="button" className="btn-primary text-xs" onClick={() => onDrillRow(row)}>
                        Ver
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!loading && !(drill?.drill?.rows || []).length ? (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-slate-500">
                    Sin pagos confirmados en el periodo
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
